import React from 'react';
import styled from 'styled-components';

import Switch from '../../components/switch-control';
import WilliamHillRewardsLogo from './william-hill-rewards-logo';
import CaesarsRewardsLogo from './caesars-rewards-logo';
import { useIsBetWithCredits, useToggleBetWithCredits } from './state';
import { convertRewardUnitsToCashUnits, formatCredits } from './convert-credits';
import { useSelector } from 'react-redux';
import { StoreState } from '../../store/types';
import { Text } from '../../components/typography';
import { formatMoneyWithSymbol } from '../../lib/money-formatter';
import { unitsValueMoney } from '../../lib/money';
import { useRewardTerminologyForPlayerCardOperator } from './reward-terminology';

export type Props = {
  credits: number;
  selectionId: string;
};

export default function BetToggle({ credits, selectionId }: Props) {
  const toggleBetWithCredits = useToggleBetWithCredits(selectionId);
  const selected = useIsBetWithCredits(selectionId);
  const handleToggle = () => toggleBetWithCredits();
  const deviceInfo = useSelector<StoreState, StoreState['device']['info']>(({ device }) => device.info);
  const { unit: rewardUnit } = useRewardTerminologyForPlayerCardOperator();
  const playerCardOperator = deviceInfo?.playerCardOperator;
  const Logo = playerCardOperator === 'CAESARS' ? CaesarsRewardsLogo : WilliamHillRewardsLogo;

  return (
    <Wrapper selected={selected}>
      <Logo />
      <Content selected={selected}>
        <Text size="lg" as="h1">
          Bet with Reward {rewardUnit}
        </Text>
        <Text size="sm" as="p">
          {formatCredits(credits)} available (
          {formatMoneyWithSymbol(
            unitsValueMoney(convertRewardUnitsToCashUnits({ rewardsUnits: credits, playerCardOperator })),
            '$'
          )}
          )
        </Text>
      </Content>
      <Control>
        <Switch id={selectionId} checked={selected} onChange={handleToggle} />
      </Control>
    </Wrapper>
  );
}

const Wrapper = styled.div<{ selected: boolean }>`
  align-items: center;
  background-color: ${({ theme, selected }) =>
    selected ? theme.components.betWithCredits.background : theme.colors.white};
  border: 1px solid
    ${({ theme, selected }) => (selected ? theme.components.betWithCredits.border : theme.colors.grey300)};
  border-radius: 6px;
  display: flex;
  justify-content: flex-start;
  padding: 1.2rem 2rem;
  width: 100%;
  * {
    margin: 0;
  }
`;

const Content = styled.div<{ selected: boolean }>`
  color: ${({ theme, selected }) =>
    selected ? theme.components.betWithCredits.text : theme.components.global.textColor};
  margin-left: 1rem;
`;

const Control = styled.div`
  margin-left: auto;
`;
